import { motionConfig, rowTapTransition, tableRowVariants } from 'animations/motionConfig';
import { useReducedMotion } from 'animations/useReducedMotion';
import { animate as animeAnimate } from 'animejs';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, MouseEvent, ReactNode, useEffect, useMemo, useRef } from 'react';
import { useExpandedRowOptional } from './ExpandedRowContext';
import type { DetailField } from './flattenRecordForDetail';
import { flattenRecordForDetail } from './flattenRecordForDetail';
import RowDetailPanel from './RowDetailPanel';

interface AnimatedTableRowProps {
    children: ReactNode;
    className?: string;
    /** Unique id for expand/collapse; required when using detailRecord or expandContent */
    rowKey?: string | number;
    /** Auto-build detail panel from row object */
    detailRecord?: unknown;
    /** Custom detail fields (overrides detailRecord flattening) */
    detailFields?: DetailField[];
    /** Fully custom expanded content */
    expandContent?: ReactNode;
    expandTitle?: string;
    onClick?: () => void;
    /** Disable row expand on click (e.g. modal tables) */
    expandable?: boolean;
}

const isInteractiveTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    return Boolean(
        target.closest(
            'button, a, input, select, textarea, label, [data-no-row-toggle], .no-row-toggle'
        )
    );
};

const AnimatedTableRow: FC<AnimatedTableRowProps> = ({
    children,
    className = '',
    rowKey,
    detailRecord,
    detailFields,
    expandContent,
    expandTitle,
    onClick,
    expandable = true,
}) => {
    const reducedMotion = useReducedMotion();
    const rowRef = useRef<HTMLTableRowElement>(null);
    const expandCtx = useExpandedRowOptional();

    const canExpand =
        expandable &&
        expandCtx &&
        rowKey !== undefined &&
        (expandContent !== undefined || detailFields !== undefined || detailRecord !== undefined);

    const expanded = canExpand && expandCtx ? expandCtx.isExpanded(rowKey!) : false;

    const fields = useMemo(() => {
        if (detailFields?.length) {
            return detailFields;
        }
        if (detailRecord !== undefined) {
            return flattenRecordForDetail(detailRecord);
        }
        return [];
    }, [detailFields, detailRecord]);

    const hasDetail = canExpand && (expandContent !== undefined || fields.length > 0);

    useEffect(() => {
        if (reducedMotion || !rowRef.current || expanded) {
            return;
        }
        const row = rowRef.current;
        animeAnimate(row, {
            backgroundColor: ['rgba(189, 144, 46, 0.12)', 'rgba(248, 250, 252, 1)'],
            duration: motionConfig.accentDuration,
            ease: 'outQuad',
        });
    }, [reducedMotion, expanded]);

    const handleClick = (e: MouseEvent<HTMLTableRowElement>) => {
        if (isInteractiveTarget(e.target)) {
            return;
        }
        if (canExpand && hasDetail) {
            expandCtx!.toggleRow(rowKey!);
        }
        onClick?.();
    };

    const rowClassName = [
        className,
        'animated-table-row',
        canExpand && hasDetail ? 'animated-table-row--expandable' : '',
        expanded ? 'active' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const mainRow = reducedMotion ? (
        <tr
            ref={rowRef}
            className={rowClassName}
            onClick={hasDetail || onClick ? handleClick : undefined}
            role={hasDetail ? 'button' : undefined}
            tabIndex={hasDetail ? 0 : undefined}
            onKeyDown={
                hasDetail
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              expandCtx?.toggleRow(rowKey!);
                          }
                      }
                    : undefined
            }
        >
            {children}
        </tr>
    ) : (
        <motion.tr
            ref={rowRef}
            variants={tableRowVariants}
            className={rowClassName}
            onClick={hasDetail || onClick ? handleClick : undefined}
            whileTap={hasDetail ? { scale: 0.995 } : undefined}
            transition={rowTapTransition}
            layout={false}
            role={hasDetail ? 'button' : undefined}
            tabIndex={hasDetail ? 0 : undefined}
            onKeyDown={
                hasDetail
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              expandCtx?.toggleRow(rowKey!);
                          }
                      }
                    : undefined
            }
        >
            {children}
        </motion.tr>
    );

    const detailContent = expandContent ?? <RowDetailPanel fields={fields} title={expandTitle} />;

    const detailRowInner = (
        <td colSpan={expandCtx?.columnCount ?? 1} className="!p-0 !border-0">
            <div className="animated-table-row-detail__inner">{detailContent}</div>
        </td>
    );

    if (!hasDetail) {
        return mainRow;
    }

    const fragmentKey = rowKey !== undefined ? String(rowKey) : undefined;

    return (
        <React.Fragment key={fragmentKey}>
            {mainRow}
            <AnimatePresence initial={false}>
                {expanded &&
                    (reducedMotion ? (
                        <tr key={`detail-${rowKey}`} className="animated-table-row-detail">
                            {detailRowInner}
                        </tr>
                    ) : (
                        <motion.tr
                            key={`detail-${rowKey}`}
                            className="animated-table-row-detail"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.22, ease: motionConfig.rowEase }}
                        >
                            {detailRowInner}
                        </motion.tr>
                    ))}
            </AnimatePresence>
        </React.Fragment>
    );
};

export default AnimatedTableRow;
